import {dollarsToCpmMicros, mediationLineDisplayNameForSubmit} from '../mediationDraft.ts';
import type {DryRunStep, MissingApiNote} from '../mediationDraft.ts';
import {
    MINTEGRAL_SOURCE_ID,
    mintegralAdType,
    parseMintegralUnitIdFromApplyData,
    resolveMintegralAdapter,
} from './partnerAdapters.ts';
import type {BuildConfirmStepsContext, BuildConfirmStepsResult, WaterfallSourceAdapter} from './types.ts';

export {parseMintegralUnitIdFromApplyData};

export type BuildMintegralMappingChangeInput = {
    appKey: string;
    appId: string;
    placementId: string;
    unitId: string;
    displayName?: string;
    platform?: string;
    format?: string;
};

export function buildMintegralMappingChange(
    input: BuildMintegralMappingChangeInput,
): Record<string, unknown> {
    const resolved = resolveMintegralAdapter(input.platform, input.format);
    const change: Record<string, unknown> = {
        adapterId: resolved.adapterId,
        adSourceId: MINTEGRAL_SOURCE_ID,
        adUnitConfigurations: {
            [resolved.keys.appKey]: String(input.appKey || '').trim(),
            [resolved.keys.appId]: String(input.appId || '').trim(),
            [resolved.keys.placementId]: String(input.placementId || '').trim(),
            [resolved.keys.adUnitId]: String(input.unitId || '').trim(),
        },
    };
    const displayName = String(input.displayName || '').trim();
    if (displayName) {
        change.displayName = displayName;
    }
    return change;
}

export type BuildMintegralUnitCreateChangeInput = {
    unitName: string;
    floorDollars?: number | null;
};

export function buildMintegralUnitCreateChange(
    input: BuildMintegralUnitCreateChangeInput,
): Record<string, unknown> {
    const change: Record<string, unknown> = {
        unit_name: String(input.unitName || '').trim(),
        bidding_type: 0,
    };
    if (input.floorDollars != null && !Number.isNaN(Number(input.floorDollars))) {
        change.target_ecpm = {ALL: Number(input.floorDollars)};
    }
    return change;
}

function mappingPathFor(ctx: BuildConfirmStepsContext): string {
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    return mappingResourceId
        ? `/admob/accounts/${ctx.accountId}/ad-units/${mappingResourceId}/mappings`
        : `/admob/accounts/${ctx.accountId}/ad-units/{standard_ad_unit_resource_id}/mappings`;
}

function buildAddWaterfallLineStep(
    ctx: BuildConfirmStepsContext,
    opts: {
        stepId: string;
        dependsOn: string;
        displayName: string;
        notes: string[];
        tempId: string;
        micros: string | null;
    },
): DryRunStep {
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    const lineBody: Record<string, any> = {
        id: opts.tempId,
        displayName: opts.displayName,
        adSourceId: MINTEGRAL_SOURCE_ID,
        cpmMode: 'MANUAL',
        state: 'ENABLED',
    };
    if (opts.micros != null) {
        lineBody.cpmMicros = opts.micros;
    }
    return {
        id: opts.stepId,
        kind: 'add-waterfall-line',
        label: `绑定瀑布流行 ${opts.displayName}`,
        method: 'PATCH',
        path: ctx.patchPath,
        change: {
            updateMask: `mediationGroupLines["${opts.tempId}"]`,
            mediationGroupLines: {[opts.tempId]: lineBody},
        },
        notes: opts.notes,
        dependsOn: opts.dependsOn,
        rowKey: ctx.row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    };
}

function requireMintegralCtx(
    ctx: BuildConfirmStepsContext,
    rowKey: string,
    displayLabel: string,
): MissingApiNote[] {
    const missing: MissingApiNote[] = [];
    if (!ctx.standardAdUnitId) {
        missing.push({
            id: `no-targeting-ad-unit-${rowKey}`,
            message: `新建行「${displayLabel}」缺少中介组 targeting.adUnitIds[0]，无法串联 Mintegral → mapping → 瀑布流行。`,
        });
    }
    if (!String(ctx.mintegralAppKey || '').trim() || !String(ctx.mintegralAppId || '').trim()) {
        missing.push({
            id: `no-mintegral-app-${rowKey}`,
            message: `新建行「${displayLabel}」缺少 Mintegral App（按 package/os 匹配），无法写 mapping。`,
        });
    }
    if (!String(ctx.mintegralPlacementId || '').trim()) {
        missing.push({
            id: `no-mintegral-placement-${rowKey}`,
            message: `新建行「${displayLabel}」缺少 Mintegral Placement，无法创建/绑定 Unit。`,
        });
    }
    return missing;
}

export function buildConfirmSteps(ctx: BuildConfirmStepsContext): BuildConfirmStepsResult {
    const steps: DryRunStep[] = [];
    const missingApis: MissingApiNote[] = [];
    const warnings: string[] = [];
    const row = ctx.row;
    const displayName = row.displayName || 'mintegral_line';
    const lineDisplayName = mediationLineDisplayNameForSubmit(
        row,
        ctx.draftRows || [],
        ctx.lineLabelHint || 'Mintegral',
    );

    const required = requireMintegralCtx(ctx, row.key, row.displayName || row.key);
    if (required.length) {
        return {steps, missingApis: required, warnings};
    }

    const appKey = String(ctx.mintegralAppKey || '').trim();
    const appId = String(ctx.mintegralAppId || '').trim();
    const placementId = String(ctx.mintegralPlacementId || '').trim();
    const bindMode = row.opmcBindMode === 'existing' ? 'existing' : 'create';
    const selectedId = String(row.selectedOpmcId || '').trim();
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    const micros = dollarsToCpmMicros(row.floorPrice);
    const mappingPath = mappingPathFor(ctx);

    if (bindMode === 'existing') {
        if (!selectedId) {
            missingApis.push({
                id: `no-mintegral-unit-${row.key}`,
                message: `新建行「${row.displayName || row.key}」选择了已有 Unit，但未选择具体 Unit。`,
            });
            return {steps, missingApis, warnings};
        }
        const mappingStepId = `create-mapping-${row.key}`;
        const addLineStepId = `add-waterfall-line-${row.key}`;
        const tempId = ctx.allocTempLineId();
        const notes = ['选已有 Mintegral Unit：跳过创建；Apply 时 create-mapping → 绑定 MANUAL 行。'];
        steps.push({
            id: mappingStepId,
            kind: 'create-mapping',
            label: `创建 mapping ${displayName}`,
            method: 'POST',
            path: mappingPath,
            change: buildMintegralMappingChange({
                appKey,
                appId,
                placementId,
                unitId: selectedId,
                displayName,
                platform: ctx.platform,
                format: ctx.adFormat,
            }),
            notes: [...notes],
            rowKey: row.key,
            standardAdUnitId: ctx.standardAdUnitId,
            standardAdUnitResourceId: mappingResourceId || undefined,
        });
        steps.push(
            buildAddWaterfallLineStep(ctx, {
                stepId: addLineStepId,
                dependsOn: mappingStepId,
                displayName: lineDisplayName,
                notes: [...notes, 'dependsOn create-mapping'],
                tempId,
                micros,
            }),
        );
        return {steps, missingApis, warnings};
    }

    const notes: string[] = [
        `Apply 时串联：在 Placement ${placementId} 下创建 Unit → mapping → MANUAL 行（ad_type 期望 ${mintegralAdType(ctx.adFormat)}）。`,
    ];
    const createStepId = `create-remote-${row.key}`;
    const mappingStepId = `create-mapping-${row.key}`;
    const addLineStepId = `add-waterfall-line-${row.key}`;
    const tempId = ctx.allocTempLineId();
    const createPath = `/mintegral/apps/${appId}/placements/${placementId}/units`;

    steps.push({
        id: createStepId,
        kind: 'create-remote',
        label: `创建 Mintegral Unit ${displayName}`,
        method: 'POST',
        path: createPath,
        change: buildMintegralUnitCreateChange({
            unitName: displayName,
            floorDollars: row.floorPrice,
        }),
        notes: [...notes],
        rowKey: row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    });

    steps.push({
        id: mappingStepId,
        kind: 'create-mapping',
        label: `创建 mapping ${displayName}`,
        method: 'POST',
        path: mappingPath,
        change: {},
        notes: [...notes, 'dependsOn create-remote'],
        dependsOn: createStepId,
        rowKey: row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    });

    steps.push(
        buildAddWaterfallLineStep(ctx, {
            stepId: addLineStepId,
            dependsOn: mappingStepId,
            displayName: lineDisplayName,
            notes: [...notes, 'dependsOn create-mapping'],
            tempId,
            micros,
        }),
    );

    return {steps, missingApis, warnings};
}

export const mintegralAdapter: WaterfallSourceAdapter = {
    sourceId: MINTEGRAL_SOURCE_ID,
    displayNameHint: 'Mintegral',
    supportsCreate: true,
    supportsPickExisting: true,
    hasAccountApi: false,
    buildConfirmSteps,
};
